'use client';

import { FunctionComponent } from 'react';
import { Link } from '@/react/components/core-ui/Link';
import { Icon, IconTypes, IconSizeProp } from '@/react/components/core-ui/Icon';
import { EventType } from '@/services/isomorphic/UserInteractionService';
import { useDrawerActions } from '@/react/components/core-ui/Drawer/hooks';
import { useLocale } from '@/react/hooks/useLocale';
import { msg } from '@/services/isomorphic/I18NService';
import ConfigurationService from '@/services/isomorphic/ConfigurationService';
import { UtilityLinkId } from '@/configs/menu';
import { isNullish } from '@/utils/null-utils';
import { useLogout } from '@/react/hooks/useLogout';
import { AccountUtilityLink } from './AccountUtilityLink';
import { LogoutUtilityLink } from './LogoutUtilityLink';
import { MobileNavItem } from '../MobileNavItem';
import { ChooseCountryUtilityLink } from './ChooseCountryUtilityLink';

import S from '../../styles.base.module.scss';
import { header_utilBar_customerService } from "@/lang/__generated__/ahnu/header_utilBar_customerService";
import { header_utilBar_storeLocator } from "@/lang/__generated__/ahnu/header_utilBar_storeLocator";
import { account_authenticated_logout } from "@/lang/__generated__/ahnu/account_authenticated_logout";
import { header_mobileMenu_membership } from "@/lang/__generated__/ahnu/header_mobileMenu_membership";
import { header_mobileMenu_trackPackage } from "@/lang/__generated__/ahnu/header_mobileMenu_trackPackage";

/**
 * Resolves the localized display label for a utility link by its ID.
 * Uses statically analyzable `msg()` calls so resource string IDs can be
 * extracted at build time.
 * @param linkId - The {@link UtilityLinkId} to resolve a label for.
 * @returns The localized display label, or an empty string for unknown IDs.
 */
function getUtilityLinkLabel(linkId: string): string {
  switch (linkId) {
    case UtilityLinkId.CustomerService:
      return msg(header_utilBar_customerService);
    case UtilityLinkId.StoreLocator:
      return msg(header_utilBar_storeLocator);
    case UtilityLinkId.Logout:
      return msg(account_authenticated_logout);
    case UtilityLinkId.membership:
      return msg(header_mobileMenu_membership);
    case UtilityLinkId.trackPackage:
      return msg(header_mobileMenu_trackPackage);
    default:
      return '';
  }
}

/**
 * Utility links shown in the mobile navigation drawer footer.
 * The links displayed and their order are driven by the
 * `mobileMenu.utilityLinks` config. AccountUtilityLink always
 * renders first regardless of config order.
 */
export const MobileUtilityLinks: FunctionComponent = () => {
  const { isLoggedIn } = useLogout();
  const { closeAllDrawers } = useDrawerActions();
  const { language, country } = useLocale();

  const menuConfig = ConfigurationService.getConfig('menu', language, country);
  const utilityLinks = menuConfig.getSetting('mobileMenu.utilityLinks').value;

  return (
    <div className={S.utilityLinkContainer}>
      <AccountUtilityLink isLoggedIn={isLoggedIn} />

      {utilityLinks.map((link) => {
        const linkId = link.id.value;
        if (isNullish(linkId)) return null;

        if (linkId === UtilityLinkId.CountrySelector) {
          return <ChooseCountryUtilityLink key={linkId} />;
        }

        if (linkId === UtilityLinkId.Logout) {
          return <LogoutUtilityLink key={linkId} icon={link.icon.value} />;
        }

        const linkLabel = getUtilityLinkLabel(linkId);
        const linkHref = link.href.value;
        const linkIcon = link.icon.value as IconTypes;

        return (
          <MobileNavItem key={linkId}>
            <span className={S.navLink}>
              <Link
                className={S.utilityLink}
                href={linkHref}
                onClick={closeAllDrawers}
                interactionDetails={{
                  action: EventType.NavigationLink,
                  link: {
                    linkName: linkLabel.toLowerCase(),
                    linkCategory: 'top-navigation',
                    linkSection: linkLabel.toLowerCase()
                  }
                }}
              >
                <span className={S.labelContainer}>
                  <Icon
                    className={S.navLinkIcon}
                    icon={linkIcon}
                    size={IconSizeProp.Size1X}
                  />
                  {linkLabel}
                </span>
              </Link>
            </span>
          </MobileNavItem>
        );
      })}
    </div>
  );
};

export default MobileUtilityLinks;
